import React, { useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { FiUsers, FiCalendar, FiClock, FiSmile, FiSettings, FiHelpCircle } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import JoinGroupModal from './JoinGroupModal';
import NotificationBell from './NotificationBell';
import Logo from './common/Logo';
import { isNative, isIOS } from '../services/platformService';
import { getUnreadForUserCount } from '../services/supportService';

function Header({ onJoinSuccess, onSettingsClick }) {
  const location = useLocation();
  const { user } = useAuth();
  const [showJoinModal, setShowJoinModal] = useState(false);
  const [supportUnread, setSupportUnread] = useState(0);
  const isIOSNative = isNative() && isIOS();

  const refreshSupportUnread = useCallback(async () => {
    if (!user) return;
    try {
      const n = await getUnreadForUserCount();
      setSupportUnread(n);
    } catch {
      // Silent - the badge just won't appear.
    }
  }, [user]);

  useEffect(() => {
    refreshSupportUnread();
    // Refresh whenever the route changes so opening /support clears the badge
    // once the user reads their threads.
  }, [refreshSupportUnread, location.pathname]);

  const navItems = [
    { path: '/rounds', icon: FiCalendar, label: 'Rounds' },
    { path: '/friends', icon: FiSmile, label: 'Friends' },
    { path: '/groups', icon: FiUsers, label: 'Groups' },
    { path: '/my-availability', icon: FiClock, label: 'Availability' },
  ];


  // Shared nav content for both iOS and web
  const navContent = (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex justify-between items-center h-16 sm:h-20">
        {/* Logo */}
        <Link to="/" className="flex items-center space-x-2 sm:space-x-3 group">
          <motion.div
            transition={{ type: "spring", stiffness: 400, damping: 10 }}
            className="w-10 h-10 sm:w-12 sm:h-12 bg-accent rounded-lg flex items-center justify-center"
          >
            <Logo size="md" variant="white" className="w-7 h-7 sm:w-8 sm:h-8" />
          </motion.div>
          <div className="hidden sm:block">
            <h1 className="text-xl sm:text-2xl font-semibold text-text-primary">
              TeeSync
            </h1>
          </div>
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden md:flex items-center space-x-4">
          <nav className="flex space-x-2">
            {navItems.map((item) => {
              const ItemWrapper = item.onClick ? 'button' : Link;
              const wrapperProps = item.onClick
                ? { onClick: item.onClick }
                : { to: item.path };

              return (
                <ItemWrapper
                  key={item.path}
                  {...wrapperProps}
                  className={`flex items-center space-x-2 px-4 py-2.5 rounded-lg transition-all duration-200 font-medium min-h-[44px] ${
                    location.pathname === item.path
                      ? 'bg-accent/10 text-accent '
                      : 'text-text-secondary hover:text-text-primary hover:bg-surface'
                  }`}
                >
                  <SafeIcon icon={item.icon} className="w-5 h-5" />
                  <span>{item.label}</span>
                </ItemWrapper>
              );
            })}
          </nav>

          {/* Controls */}
          <div className="flex items-center space-x-1">
            <Link
              to="/support"
              className="relative p-2 text-text-secondary hover:text-text-primary transition-colors duration-200"
              aria-label="Help and support"
            >
              <SafeIcon icon={FiHelpCircle} className="w-5 h-5" />
              {supportUnread > 0 && (
                <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-red-500" aria-hidden="true" />
              )}
            </Link>
            <motion.button
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={onSettingsClick}
              className="relative p-2 text-text-secondary hover:text-text-primary transition-colors duration-200"
              aria-label="Settings"
            >
              <SafeIcon icon={FiSettings} className="w-5 h-5" />
            </motion.button>
            <NotificationBell />
          </div>
        </div>

        {/* Mobile Controls */}
        <div className="md:hidden flex items-center space-x-1">
          <Link
            to="/support"
            className="relative p-2 text-text-secondary hover:text-text-primary transition-colors duration-200"
            aria-label="Help and support"
          >
            <SafeIcon icon={FiHelpCircle} className="w-5 h-5" />
            {supportUnread > 0 && (
              <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-red-500" aria-hidden="true" />
            )}
          </Link>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={onSettingsClick}
            className="relative p-2 text-text-secondary hover:text-text-primary transition-colors duration-200"
            aria-label="Settings"
          >
            <SafeIcon icon={FiSettings} className="w-5 h-5" />
          </motion.button>
          <NotificationBell />
        </div>
      </div>
    </div>
  );

  return (
    <>
      {isIOSNative ? (
        // iOS: Sticky header - keeps content flowing below it (no need for the
        // surrounding layout to compensate with padding-top), and the bar still
        // stays pinned to the top when the page scrolls. Status bar safe-area
        // is absorbed into the header's own padding-top.
        <header
          className="sticky top-0 z-50 bg-app border-b border-border-subtle shadow-xs"
          style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
        >
          {navContent}
        </header>
      ) : (
        // Web: Standard sticky header
        <header className="sticky top-0 z-50 backdrop-blur-xl bg-app/95 border-b border-border-subtle shadow-xs">
          {navContent}
        </header>
      )}

      {/* Join Group Modal */}
      <JoinGroupModal
        isOpen={showJoinModal}
        onClose={() => setShowJoinModal(false)}
        onJoinSuccess={onJoinSuccess}
      />
    </>
  );
}

export default Header;
